import { useState } from "react";
import { Search, Wifi, Loader2, Signal, Phone, MessageSquare, Database, Calendar, CreditCard, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

function DataBar({ used, total, percent }) {
  const color = percent > 90 ? "#ef4444" : percent > 70 ? "#f97316" : "#10b981";
  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <span className="crm-stat-label">Consumo de Dados</span>
        <span className="text-xl font-extrabold" style={{ color: "var(--crm-text)" }}>{used}GB / {total}GB</span>
      </div>
      <div className="w-full h-5 rounded-full overflow-hidden" style={{ background: "var(--crm-border)" }}>
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(percent, 100)}%`, background: `linear-gradient(90deg, ${color}, ${color}dd)`, boxShadow: `0 0 10px ${color}40` }} />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-sm font-semibold" style={{ color }}>{percent}% utilizado</span>
        <span className="text-sm font-bold" style={{ color }}>{(total - used).toFixed(2)}GB restante</span>
      </div>
    </div>
  );
}

export default function SimLookup() {
  const [simNumber, setSimNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!simNumber.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/consultar-sim", { msn: simNumber });
      if (data.success) setResult(data);
      else setError(data.error || "SIM não encontrado");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao consultar.");
    }
    setLoading(false);
  };

  const statusColor = result?.status === "ACTIVE" ? "#10b981" : result?.status === "SUSPENDED" ? "#f97316" : "#ef4444";
  const statusLabel = result?.status === "ACTIVE" ? "Ativo" : result?.status === "SUSPENDED" ? "Suspenso" : result?.status || "—";

  return (
    <div data-testid="sim-lookup-page">
      <h1 className="crm-heading mb-2 flex items-center gap-2">
        <Wifi className="w-6 h-6" style={{ color: "var(--crm-accent)" }} />
        Consultar SIM
      </h1>
      <p className="text-sm mb-8" style={{ color: "var(--crm-text-muted)" }}>Consultar uso de dados e informações de qualquer SIM card NewSky Mobile</p>

      {/* Search form */}
      <form onSubmit={handleSubmit} className="flex gap-3 mb-8 max-w-lg">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
          <input value={simNumber} onChange={(e) => setSimNumber(e.target.value)} data-testid="crm-sim-input"
            placeholder="Número do telefone ou SIM..." className="crm-input pl-10" required />
        </div>
        <button type="submit" disabled={loading} className="crm-btn-primary whitespace-nowrap" data-testid="crm-sim-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? "Buscando..." : "Consultar"}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="crm-card p-4 mb-6 flex items-center gap-3" style={{ borderLeft: "3px solid #ef4444" }}>
          <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: "#ef4444" }} />
          <p className="text-sm" style={{ color: "#ef4444" }}>{error}</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div>
          {/* Status header */}
          <div className="crm-card p-6 mb-6" style={{ borderLeft: `3px solid ${statusColor}` }}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Número</p>
                <p className="text-2xl font-bold tracking-wide" style={{ color: "var(--crm-text)" }}>{result.msisdn}</p>
              </div>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: `${statusColor}15`, color: statusColor, border: `1.5px solid ${statusColor}40` }}>
                <CheckCircle2 className="w-4 h-4" /> {statusLabel}
              </span>
            </div>
            <DataBar used={result.data_used_gb} total={result.data_limit_gb} percent={result.usage_percent} />
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { icon: Database, label: "Plano", value: result.plan_code, color: "#f97316" },
              { icon: Signal, label: "Rede", value: result.communication, color: "#3b82f6" },
              { icon: CreditCard, label: "SIM", value: result.card_size, color: "#8b5cf6" },
              { icon: Phone, label: "Voz", value: result.voice === "ON" ? "Ativo" : "Inativo", color: "#10b981" },
              { icon: MessageSquare, label: "SMS", value: result.sms === "ON" ? "Ativo" : "Inativo", color: "#06b6d4" },
              { icon: Calendar, label: "Início", value: result.plan_start, color: "#f59e0b" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="crm-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}15`, color: item.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{item.label}</span>
                  </div>
                  <p className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>{item.value}</p>
                </div>
              );
            })}
          </div>

          {/* Details table */}
          <div className="crm-card overflow-hidden">
            <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--crm-border)" }}>
              <p className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>Detalhes do SIM</p>
            </div>
            <div className="overflow-x-auto">
              <table className="crm-table">
                <tbody>
                  {[
                    ["Serial Number", result.sn],
                    ["Ativação", result.first_day],
                    ["Ligações", result.call_flat === "70FLAT" ? "Ilimitadas (70 min/ligação)" : result.call_flat],
                    ["Início do Plano", result.plan_start],
                    ["Fim do Plano", result.plan_end === "-" ? "Sem vencimento" : result.plan_end],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ color: "var(--crm-text-muted)", width: "200px" }}>{label}</td>
                      <td className="primary">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* New search */}
          <div className="mt-6">
            <button onClick={() => { setResult(null); setSimNumber(""); }} className="crm-btn-secondary text-sm">
              <Search className="w-4 h-4" /> Nova consulta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
